import gql from 'graphql-tag'
import ApolloClient from 'apollo-client'

import { NormalizedCacheObject } from 'apollo-cache-inmemory'

import { cacheGQLQuery } from '@thg-commerce/enterprise-core/src/cache/graphql'
import { sanitizeHtml } from '@thg-commerce/enterprise-utils/src/Security/cookieUtilities/safeHtml'
import { defaults as sitePropertyDefaults } from '@thg-commerce/enterprise-network/src/ApolloProvider'
import { SitePropertiesQueryVariables } from '@thg-commerce/enterprise-network/src/generated/graphql'

import { Configuration, SiteProperties } from '../ConfigurationLoader/types'

import pkg from '../../package.json'

const GQL_CACHE_PREFIX = `core:${pkg.version}`

const SITE_PROPERTIES_QUERY = gql`
  query SiteProperties(
    $propertyBucket: String
    $previewId: String
    $keys: [String!]
  ) {
    siteProperties(
      propertyBucket: $propertyBucket
      previewId: $previewId
      keys: $keys
    ) @client {
      properties {
        key
        value
        location
      }
    }
  }
`

export const loadSiteProperties = async (
  apollo: ApolloClient<NormalizedCacheObject>,
  config: Configuration,
  previewId?: string,
): Promise<SiteProperties> => {
  const supportedSitePropertyKeys = Object.keys(sitePropertyDefaults)
  let siteProperties: SiteProperties = {}
  try {
    siteProperties =
      (await cacheGQLQuery({
        config,
        query: SITE_PROPERTIES_QUERY,
        ttl: 3600,
        cachePrefix: GQL_CACHE_PREFIX,
        lookup: async () => {
          const { data } = await apollo.query<
            {
              siteProperties: {
                properties: {
                  key: string
                  value: string
                  location: string
                }[]
              }
            },
            SitePropertiesQueryVariables
          >({
            query: SITE_PROPERTIES_QUERY,
            variables: {
              propertyBucket: config.publicRuntimeConfig.SITE_PROPERTIES_BUCKET,
              previewId: previewId || '',
              keys: supportedSitePropertyKeys,
            },
          })

          const indexedProperties = data.siteProperties.properties.reduce<
            SiteProperties
          >((accumulator, property) => {
            accumulator[property.key] = {
              value: sanitizeHtml(property.value, {}),
              location: property.location,
            }
            return accumulator
          }, {})

          return indexedProperties
        },
      })) || {}
  } catch (e) {
    console.warn(`Failed to load site properties with error ${e.message}`)
  }
  return siteProperties
}
